import React, { useCallback, useEffect, useState } from "react";
import { QrCode, Clock, MapPin, Plus, XCircle, IndianRupee, Loader2, History } from "lucide-react";
import { toast } from "sonner";
import { Spinner, EmptyState } from "../../../components/common";
import { Card, CardContent } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { Label } from "../../../components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../../../components/ui/alert-dialog";
import { api, errMsg, fmtINR, fmtDateTime } from "../../../lib/api";
import { PStatus, VehicleTypeBadge, fmtMinutes } from "../../../components/parking/common";
import { PayModePicker } from "../../../components/parking/PayFlow";

/* Phase 201 — My Bookings: live active / upcoming cards with countdown, QR ticket, extend, cancel (refund to wallet),
   pay what is due (incl. overstay), plus history. */

const EXTENSIONS = [30, 60, 120];

const endsIn = (b) => {
  const m = b.ends_in_minutes;
  if (m == null) return null;
  if (b.status === "reserved") {
    const startIn = Math.round((new Date(b.start_at) - Date.now()) / 60000);
    if (startIn > 0) return { text: `Starts in ${fmtMinutes(startIn)}`, tone: "text-sky-700" };
  }
  if (m < 0) return { text: `Over by ${fmtMinutes(-m)}`, tone: "text-destructive" };
  if (m <= 10) return { text: `Ends in ${fmtMinutes(m)}`, tone: "text-[hsl(var(--warning))]" };
  return { text: `Ends in ${fmtMinutes(m)}`, tone: "text-[hsl(var(--success))]" };
};

export function BookingsTab({ ctx }) {
  const { code, live, startPay, showTicket, refresh, walletBalance } = ctx;
  const [active, setActive] = useState(null);
  const [history, setHistory] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [extend, setExtend] = useState(null);
  const [cancel, setCancel] = useState(null);
  const [payFor, setPayFor] = useState(null);
  const [settings, setSettings] = useState(null);

  const load = useCallback(() => {
    api.get(`/t/${code}/parking/citizen/bookings?scope=active`).then((r) => setActive(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get(`/t/${code}/parking/citizen/bookings?scope=history&limit=30`).then((r) => setHistory(r.data)).catch(() => {});
  }, [code]);
  useEffect(() => { load(); }, [load, live]);
  useEffect(() => { api.get(`/t/${code}/parking/citizen/locations`).then((r) => setSettings({ ...r.data.settings, fastag_available: r.data.fastag_available })).catch(() => {}); }, [code]);

  const pay = (b, mode) => {
    api.post(`/t/${code}/parking/citizen/bookings/${b.id}/pay`, { payment_mode: mode })
      .then((r) => {
        setPayFor(null);
        if (r.data.status === "paid") { toast.success(`Paid ${fmtINR(r.data.amount_inr)} from wallet`); refresh(); return; }
        startPay(r.data, { title: "Pay parking due", lines: [{ label: "Booking", value: b.booking_no }, { label: "Vehicle", value: b.vehicle_number }], onPaid: () => { toast.success("Payment received"); refresh(); } });
      })
      .catch((e) => toast.error(errMsg(e)));
  };

  if (!active) return <Spinner />;
  return (
    <div className="space-y-4" data-testid="citizen-parking-bookings">
      {active.length === 0 ? (
        <EmptyState title="No active or upcoming parking" subtitle="Book a slot from Find & Book — your QR ticket will appear here." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {active.map((b) => {
            const e = endsIn(b);
            return (
              <Card key={b.id} className="shadow-sm" data-testid={`booking-card-${b.booking_no}`}>
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-mono text-xs text-muted-foreground">{b.booking_no}</p>
                      <p className="font-semibold font-heading flex items-center gap-1.5 leading-tight"><MapPin className="h-4 w-4 text-primary shrink-0" /> <span className="truncate">{b.location_name}</span></p>
                    </div>
                    <PStatus status={b.status} />
                  </div>
                  <div className="flex items-center gap-2 flex-wrap text-sm">
                    <VehicleTypeBadge type={b.vehicle_type} short /> <span className="font-mono font-medium">{b.vehicle_number}</span>
                    {e && <span className={`ml-auto text-xs font-semibold ${e.tone}`} data-testid={`booking-ends-${b.booking_no}`}><Clock className="h-3 w-3 inline mr-1" />{e.text}</span>}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    <p>{fmtDateTime(b.start_at)} → {fmtDateTime(b.end_at)} · {fmtMinutes(b.duration_minutes)}</p>
                    <p className="mt-0.5">Fare {fmtINR(b.amount)} · <PStatus status={b.payment_status} className="text-[10px] py-0" />{b.due > 0 && <span className="text-destructive font-semibold ml-1" data-testid={`booking-due-${b.booking_no}`}>Due {fmtINR(b.due)}</span>}{b.overstay_minutes > 0 && <Badge variant="outline" className="ml-1 text-[10px] border-destructive text-destructive">Overstay {fmtMinutes(b.overstay_minutes)}</Badge>}</p>
                  </div>
                  <div className="flex flex-wrap gap-2 pt-1">
                    <Button size="sm" className="h-10 flex-1" onClick={() => showTicket({ booking: b })} data-testid={`booking-qr-${b.booking_no}`}><QrCode className="h-4 w-4 mr-1.5" /> Show QR</Button>
                    {b.due > 0 && <Button size="sm" variant="secondary" className="h-10" onClick={() => setPayFor(b)} data-testid={`booking-pay-${b.booking_no}`}><IndianRupee className="h-4 w-4 mr-1" /> Pay {fmtINR(b.due)}</Button>}
                    <Button size="sm" variant="outline" className="h-10" onClick={() => setExtend(b)} data-testid={`booking-extend-${b.booking_no}`}><Plus className="h-4 w-4 mr-1" /> Extend</Button>
                    {b.status === "reserved" && <Button size="sm" variant="ghost" className="h-10 text-destructive" onClick={() => setCancel(b)} data-testid={`booking-cancel-${b.booking_no}`}><XCircle className="h-4 w-4 mr-1" /> Cancel</Button>}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <div>
        <Button variant="ghost" size="sm" onClick={() => setShowHistory(!showHistory)} data-testid="bookings-history-toggle"><History className="h-4 w-4 mr-1.5" /> {showHistory ? "Hide" : "Show"} history ({history?.length ?? 0})</Button>
        {showHistory && (
          <div className="mt-2 space-y-2" data-testid="bookings-history">
            {!history ? <Spinner /> : history.length === 0 ? <p className="text-sm text-muted-foreground px-2">No past bookings yet.</p> : history.map((b) => (
              <div key={b.id} className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2 text-sm" data-testid={`history-row-${b.booking_no}`}>
                <div className="min-w-0"><p className="font-medium truncate">{b.location_name}</p><p className="text-xs text-muted-foreground"><span className="font-mono">{b.booking_no}</span> · {b.vehicle_number} · {fmtDateTime(b.start_at)}</p></div>
                <div className="text-right shrink-0"><p className="tabular-nums font-medium">{fmtINR(b.amount)}</p><PStatus status={b.status} className="text-[10px]" /></div>
              </div>
            ))}
          </div>
        )}
      </div>

      {extend && <ExtendDialog code={code} booking={extend} settings={settings} walletBalance={walletBalance} onClose={() => setExtend(null)}
        onDone={({ booking: b, payment }) => {
          setExtend(null);
          if (payment?.status === "paid") { toast.success(`Extended till ${fmtDateTime(b.end_at)} (paid from wallet)`); refresh(); return; }
          startPay(payment, { title: "Pay for extension", lines: [{ label: "Booking", value: b.booking_no }, { label: "New end", value: fmtDateTime(b.end_at) }], onPaid: () => { toast.success(`Extended till ${fmtDateTime(b.end_at)}`); refresh(); } });
        }} />}

      <AlertDialog open={!!cancel} onOpenChange={(o) => !o && setCancel(null)}>
        <AlertDialogContent data-testid="booking-cancel-dialog">
          <AlertDialogHeader><AlertDialogTitle>Cancel booking {cancel?.booking_no}?</AlertDialogTitle><AlertDialogDescription>The slot is released for others. {cancel?.paid_amount > 0 ? `The ${fmtINR(cancel.paid_amount)} you paid is refunded to your parking wallet instantly.` : "Nothing has been charged."}</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Keep booking</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="booking-cancel-confirm" onClick={async () => { try { const r = await api.post(`/t/${code}/parking/citizen/bookings/${cancel.id}/cancel`, { reason: "Cancelled by citizen" }); toast.success(r.data.refund ? `Cancelled — ${fmtINR(r.data.refund.amount)} refunded to wallet` : "Booking cancelled"); setCancel(null); refresh(); } catch (e) { toast.error(errMsg(e)); } }}>Cancel booking</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {payFor && <PayDueDialog booking={payFor} settings={settings} walletBalance={walletBalance} onClose={() => setPayFor(null)} onPay={(mode) => pay(payFor, mode)} />}
    </div>
  );
}

function ExtendDialog({ code, booking: b, settings, walletBalance, onClose, onDone }) {
  const [mins, setMins] = useState(60);
  const [mode, setMode] = useState((settings?.payment_modes_citizen || ["upi"])[0] || "upi");
  const [busy, setBusy] = useState(false);
  const maxExtra = Math.max((Number(settings?.max_duration_hours || 12) * 60) - Number(b.duration_minutes || 0), 0);
  const go = async () => {
    setBusy(true);
    try { const r = await api.post(`/t/${code}/parking/citizen/bookings/${b.id}/extend`, { extra_minutes: mins, payment_mode: mode }); onDone(r.data); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="extend-dialog">
        <DialogHeader><DialogTitle className="font-heading">Extend {b.booking_no}</DialogTitle><DialogDescription>Currently till {fmtDateTime(b.end_at)}. Each further started hour is charged at the hourly rate. Up to {fmtMinutes(maxExtra)} more.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2" data-testid="extend-options">{EXTENSIONS.filter((m) => m <= maxExtra).map((m) => <button key={m} type="button" onClick={() => setMins(m)} data-testid={`extend-${m}`} className={`rounded-full border px-3 py-2 text-sm min-h-[40px] ${mins === m ? "border-primary bg-primary text-primary-foreground" : "hover:bg-accent"}`}>+{fmtMinutes(m)}</button>)}</div>
          {maxExtra < 15 && <p className="text-sm text-destructive">This booking has reached the maximum duration.</p>}
          <div><Label className="text-xs">Pay with</Label><div className="mt-1"><PayModePicker modes={settings?.payment_modes_citizen} value={mode} onChange={setMode} walletBalance={walletBalance} fastagAvailable={settings?.fastag_available} testId="extend-pay-mode" /></div></div>
        </div>
        <DialogFooter className="gap-2 sm:gap-0"><Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button><Button className="h-11" onClick={go} disabled={busy || maxExtra < 15} data-testid="extend-submit">{busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-1.5" />} Extend {fmtMinutes(mins)}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PayDueDialog({ booking: b, settings, walletBalance, onClose, onPay }) {
  const [mode, setMode] = useState((settings?.payment_modes_citizen || ["upi"])[0] || "upi");
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="pay-due-dialog">
        <DialogHeader><DialogTitle className="font-heading">Pay {fmtINR(b.due)} for {b.booking_no}</DialogTitle><DialogDescription>{b.overstay_minutes > 0 ? `Includes overstay of ${fmtMinutes(b.overstay_minutes)}. ` : ""}{b.vehicle_number} at {b.location_name}.</DialogDescription></DialogHeader>
        <PayModePicker modes={settings?.payment_modes_citizen} value={mode} onChange={setMode} walletBalance={walletBalance} fastagAvailable={settings?.fastag_available} amount={b.due} testId="due-pay-mode" />
        <DialogFooter className="gap-2 sm:gap-0"><Button variant="outline" onClick={onClose}>Cancel</Button><Button className="h-11" onClick={() => onPay(mode)} data-testid="pay-due-submit"><IndianRupee className="h-4 w-4 mr-1.5" /> Pay {fmtINR(b.due)}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
